import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, AlertTriangle, Ban } from "lucide-react";
import { Combobox } from "@/components/Combobox";
import { QuickAddDialog } from "@/components/QuickAddDialog";
import { withRegiao } from "@/lib/bairroOptions";
import { hasPerm, isAdminLike } from "@/lib/roles";
import { fmtBRL } from "@/lib/api";
import { formatTelBR, normalizePhoneBR } from "@/lib/phoneUtils";
import { computeIsReceitaRevenda, computeIsSemTroco } from "./loteHelpers";
import { DupCallout } from "./DupCallout";
import { LoteRotaCell } from "./LoteRotaCell";
import { getMissingRequiredFields, isTipoSemFormaObrigatoria } from "@/pages/entregas/entregasValidation";
import { ClienteAutocomplete, usePedidoLookup } from "@/components/ClienteAutocomplete";
import { WhatsAppFieldStatus } from "@/components/WhatsAppStatusIcon";

/**
 * Tabela de linhas do lote (uma entrega por linha).
 *
 * fev/2026 (v4): Duplicidade categorizada por STATUS:
 *  - status ∈ {aguardando, em_transito, concluida} → TRAVA salvamento (vermelho)
 *  - status ∈ {remarcar, cancelada} → alerta amarelo, permite salvar
 *  - Colisão entre linhas do MESMO lote → sempre TRAVA
 *  Aviso maior, com chip visual do status e nome do cliente da entrega duplicada.
 */
export function LoteRowsTable({
  rows, updateRow, addRow, removeRow,
  bairros, tipos, formas, regioes = [], rotas = [], user, onCatalogAdd,
  duplicateInfo,
}) {
  const [qa, setQa] = useState(null);
  const lookupPedido = usePedidoLookup();
  const bairroOptions = useMemo(() => withRegiao(bairros, regioes), [bairros, regioes]);

  const canAddBairro = isAdminLike(user) || hasPerm(user, "bairros", "i");
  const canAddTipo = isAdminLike(user) || hasPerm(user, "tipos_entrega", "i");
  const canAddForma = isAdminLike(user) || hasPerm(user, "formas_pgto", "i");

  const openQuickAdd = (rowIndex, kind) => {
    if (kind === "bairro") {
      setQa({ resource: "bairros", endpoint: "bairros", label: "Bairro",
        field: "bairro_id", rowIndex,
        extras: [{ key: "regiao_id", label: "Região", options: regioes, required: true }] });
    } else if (kind === "tipo") {
      setQa({ resource: "tipos_entrega", endpoint: "tipos-entrega", label: "Tipo de Entrega",
        field: "tipo_id", rowIndex, extras: [] });
    } else if (kind === "forma") {
      setQa({ resource: "formas_pgto", endpoint: "formas-pgto", label: "Forma de Pagamento",
        field: "forma_pgto_id", rowIndex, extras: [] });
    }
  };

  // Autofill ao sair do Nº Pedido — reaproveita cliente/tel/bairro do histórico.
  const onPedidoBlur = (i, row) => async () => {
    const numero = String(row.numero_pedido || "").trim();
    if (!numero) return;
    await lookupPedido(numero, (found) => {
      if (found.cliente_nome && !String(row.cliente_nome || "").trim()) {
        updateRow(i, "cliente_nome", found.cliente_nome);
      }
      if (found.cliente_telefone && !String(row.cliente_telefone || "").trim()) {
        updateRow(i, "cliente_telefone", formatTelBR(found.cliente_telefone));
      }
      if (found.bairro_id && !String(row.bairro_id || "").trim()) {
        updateRow(i, "bairro_id", found.bairro_id);
      }
    });
  };

  // Normaliza tel no blur (auto-9 pra celular antigo)
  const onTelBlur = (i, row) => () => {
    const cur = String(row.cliente_telefone || "").trim();
    if (!cur) return;
    const norm = normalizePhoneBR(cur);
    const formatted = formatTelBR(norm);
    if (formatted !== cur) updateRow(i, "cliente_telefone", formatted);
  };

  // Fill do autocomplete de Cliente — preenche todos os campos vazios.
  const onClienteFill = (i, row) => (s) => {
    if (s.cliente_nome) updateRow(i, "cliente_nome", s.cliente_nome);
    if (s.cliente_telefone) updateRow(i, "cliente_telefone", formatTelBR(s.cliente_telefone));
    if (s.bairro_id && !String(row.bairro_id || "").trim()) {
      updateRow(i, "bairro_id", s.bairro_id);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <div className="text-sm font-bold">Linhas ({rows.length})</div>
        <Button size="sm" variant="outline" onClick={addRow} data-testid="lote-add-row" className="border-orange-600 text-orange-700 hover:bg-orange-50">
          <Plus className="w-4 h-4 mr-1" />Adicionar linha
        </Button>
      </div>
      <div className="text-[11px] text-slate-600 mb-2">
        <span className="text-rose-600 font-semibold">*</span> Campos obrigatórios ·
        <span className="ml-1 text-slate-500 italic">Forma Pgto é opcional em Coleta/Transferência</span>
      </div>
      <div className="border border-orange-100 rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-orange-50/60">
              <TableHead className="w-10">#</TableHead>
              <TableHead className="min-w-[170px]">Bairro <span className="text-rose-600" aria-hidden>*</span></TableHead>
              {rotas.length > 0 && <TableHead className="min-w-[120px]">Rota</TableHead>}
              <TableHead className="min-w-[130px]">Cliente <span className="text-rose-600" aria-hidden>*</span></TableHead>
              <TableHead className="min-w-[130px]">Telefone <span className="text-rose-600" aria-hidden>*</span></TableHead>
              <TableHead className="min-w-[150px]">Tipo <span className="text-rose-600" aria-hidden>*</span></TableHead>
              <TableHead className="min-w-[160px]">Forma Pgto <span className="text-rose-600" aria-hidden>*</span></TableHead>
              <TableHead className="w-14">Qtd</TableHead>
              <TableHead className="min-w-[240px]">Nº do(s) Pedido(s) <span className="text-rose-600" aria-hidden>*</span></TableHead>
              <TableHead className="min-w-[130px]">Valor</TableHead>
              <TableHead className="min-w-[110px]">Troco Para</TableHead>
              <TableHead className="min-w-[120px]">Troco</TableHead>
              <TableHead className="min-w-[160px]">Observação</TableHead>
              <TableHead className="w-16 text-center">Receita</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => {
              const rowIsReceitaRevenda = computeIsReceitaRevenda(r.tipo_id, tipos);
              const rowIsSemTroco = computeIsSemTroco(r.forma_pgto_id, formas);
              const qtd = Math.max(1, parseInt(r.qtd_pedidos, 10) || 1);
              const peds = Array.isArray(r.pedidos_multiplos) ? r.pedidos_multiplos : [];
              const dup = duplicateInfo?.get(i);
              const dupClass = dup
                ? (dup.blocking ? "bg-rose-50 hover:bg-rose-100/70" : "bg-amber-50 hover:bg-amber-100/70")
                : "";
              // fev/2026: valida campos obrigatórios linha-a-linha. Regras:
              // bairro/tipo/cliente/tel/pedido sempre; forma opcional pra Coleta/Transferência.
              // Só destaca se a linha já tem QUALQUER conteúdo — linhas vazias
              // não incomodam o usuário até ele começar a preencher.
              const rowHasContent =
                r.bairro_id || r.tipo_id || r.cliente_nome || r.cliente_telefone ||
                r.numero_pedido || r.numero_pedido_receita || r.numero_pedido_revenda ||
                Number(r.valor) > 0 ||
                (Array.isArray(r.pedidos_multiplos) && r.pedidos_multiplos.some(Boolean));
              const missing = rowHasContent ? getMissingRequiredFields(r, tipos) : new Set();
              const formaOpcionalLinha = isTipoSemFormaObrigatoria(r.tipo_id, tipos);
              const ringIfMissing = (k) => missing.has(k) ? "ring-2 ring-rose-500 rounded-md" : "";
              return (
                <TableRow
                  key={r._uid}
                  data-testid={`lote-row-${i}`}
                  data-duplicate={dup ? "true" : "false"}
                  data-blocking={dup?.blocking ? "true" : "false"}
                  className={`${qtd > 1 || rowIsReceitaRevenda ? "align-top" : ""} ${dupClass}`}
                >
                  <TableCell className="text-xs text-muted-foreground pt-3">
                    <div className="flex items-center gap-1">
                      <span>{i + 1}</span>
                      {dup && (
                        <span title={dup.matches.map((m) => m.number).join(", ")}>
                          {dup.blocking
                            ? <Ban className="w-4 h-4 text-rose-600" data-testid={`lote-dup-icon-${i}`} />
                            : <AlertTriangle className="w-4 h-4 text-amber-600" data-testid={`lote-dup-icon-${i}`} />}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 items-center">
                      <div className={`flex-1 min-w-0 ${ringIfMissing("bairro_id")}`}>
                        <Combobox options={bairroOptions} value={r.bairro_id} onChange={(v) => updateRow(i, "bairro_id", v)} placeholder="—" testId={`lote-bairro-${i}`} />
                      </div>
                      {canAddBairro && (
                        <Button type="button" size="icon" variant="outline"
                          onClick={() => openQuickAdd(i, "bairro")}
                          className="h-9 w-8 shrink-0 border-orange-300 text-orange-700 hover:bg-orange-100 hover:text-orange-800"
                          title="Cadastrar novo bairro"
                          aria-label="Cadastrar novo bairro"
                          data-testid={`lote-quickadd-bairro-${i}`}>
                          <Plus className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                  {rotas.length > 0 && (
                    <TableCell className="pt-2.5">
                      <LoteRotaCell row={r} index={i} rotas={rotas} onChange={(v) => updateRow(i, "rota_id", v)} />
                    </TableCell>
                  )}
                  <TableCell>
                    <ClienteAutocomplete
                      value={r.cliente_nome}
                      onChange={(v) => updateRow(i, "cliente_nome", v)}
                      onFill={onClienteFill(i, r)}
                      placeholder="Nome"
                      testId={`lote-cliente-${i}`}
                      className={ringIfMissing("cliente_nome")}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="relative">
                      <Input
                        value={r.cliente_telefone}
                        onChange={(e) => updateRow(i, "cliente_telefone", formatTelBR(e.target.value))}
                        onBlur={onTelBlur(i, r)}
                        placeholder="(00) 00000-0000"
                        data-testid={`lote-tel-${i}`}
                        className={`h-9 text-sm mono pr-8 ${ringIfMissing("cliente_telefone")}`}
                        inputMode="numeric" />
                      <WhatsAppFieldStatus
                        phone={r.cliente_telefone}
                        className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                        testId={`wa-status-lote-${i}`}
                        showHint={false}
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 items-center">
                      <div className={`flex-1 min-w-0 ${ringIfMissing("tipo_id")}`}>
                        <Select value={r.tipo_id || "__none__"} onValueChange={(v) => updateRow(i, "tipo_id", v === "__none__" ? "" : v)}>
                          <SelectTrigger data-testid={`lote-tipo-${i}`} className="h-9 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— Nenhum —</SelectItem>
                            {tipos.map((t) => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      {canAddTipo && (
                        <Button type="button" size="icon" variant="outline"
                          onClick={() => openQuickAdd(i, "tipo")}
                          className="h-9 w-8 shrink-0 border-orange-300 text-orange-700 hover:bg-orange-100 hover:text-orange-800"
                          title="Cadastrar novo tipo de entrega"
                          aria-label="Cadastrar novo tipo de entrega"
                          data-testid={`lote-quickadd-tipo-${i}`}>
                          <Plus className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                    {rowIsReceitaRevenda && (
                      <span className="mt-1 inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide" data-testid={`lote-badge-rr-${i}`}>
                        Receita+Revenda
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 items-center">
                      <div className={`flex-1 min-w-0 ${ringIfMissing("forma_pgto_id")}`}>
                        <Select value={r.forma_pgto_id || "__none__"} onValueChange={(v) => updateRow(i, "forma_pgto_id", v === "__none__" ? "" : v)}>
                          <SelectTrigger data-testid={`lote-forma-${i}`} className="h-9 text-sm"><SelectValue placeholder={formaOpcionalLinha ? "opcional" : "—"} /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— Nenhum —</SelectItem>
                            {formas.map((f) => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      {canAddForma && (
                        <Button type="button" size="icon" variant="outline"
                          onClick={() => openQuickAdd(i, "forma")}
                          className="h-9 w-8 shrink-0 border-orange-300 text-orange-700 hover:bg-orange-100 hover:text-orange-800"
                          title="Cadastrar nova forma de pagamento"
                          aria-label="Cadastrar nova forma de pagamento"
                          data-testid={`lote-quickadd-forma-${i}`}>
                          <Plus className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                  {rowIsReceitaRevenda ? (
                    <>
                      <TableCell className="text-xs text-muted-foreground pt-3">—</TableCell>
                      <TableCell>
                        <div className="space-y-1.5">
                          <div>
                            <Label className="text-[10px] text-amber-800 font-semibold">Nº Pedido Receita</Label>
                            <Input value={r.numero_pedido_receita} onChange={(e) => updateRow(i, "numero_pedido_receita", e.target.value)} placeholder="Rec" data-testid={`lote-pedido-receita-${i}`} className={`h-8 text-sm ${ringIfMissing("numero_pedido_receita")}`} />
                          </div>
                          <div>
                            <Label className="text-[10px] text-amber-800 font-semibold">Nº Pedido Revenda</Label>
                            <Input value={r.numero_pedido_revenda} onChange={(e) => updateRow(i, "numero_pedido_revenda", e.target.value)} placeholder="Rev" data-testid={`lote-pedido-revenda-${i}`} className={`h-8 text-sm ${ringIfMissing("numero_pedido_revenda")}`} />
                          </div>
                          <DupCallout dup={dup} testId={`lote-dup-msg-${i}`} />
                        </div>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>
                        <Input type="number" min="1" max="20"
                          value={r.qtd_pedidos || 1}
                          onChange={(e) => updateRow(i, "qtd_pedidos", Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 1)))}
                          data-testid={`lote-qtd-${i}`}
                          className="h-9 text-sm mono w-14" />
                      </TableCell>
                      <TableCell>
                        {qtd === 1 ? (
                          <div className="space-y-1">
                            <Input
                              value={r.numero_pedido}
                              onChange={(e) => updateRow(i, "numero_pedido", e.target.value)}
                              onBlur={onPedidoBlur(i, r)}
                              placeholder="Nº do pedido"
                              data-testid={`lote-pedido-${i}`}
                              className={`h-9 text-sm ${ringIfMissing("numero_pedido")}`}
                            />
                            <DupCallout dup={dup} testId={`lote-dup-msg-${i}`} />
                          </div>
                        ) : (
                          <div className={`space-y-1.5 rounded-md ${missing.has("pedidos_multiplos") ? "ring-2 ring-rose-500 p-1" : ""}`} data-testid={`lote-pedidos-multi-${i}`}>
                            {Array.from({ length: qtd }, (_, k) => {
                              const val = peds[k] || "";
                              return (
                                <div key={k}>
                                  <Label className="text-[10px] text-blue-700 font-semibold">Nº do Pedido {k + 1}</Label>
                                  <Input value={val}
                                    onChange={(e) => {
                                      const cur = [...peds];
                                      while (cur.length < qtd) cur.push("");
                                      cur[k] = e.target.value;
                                      updateRow(i, "pedidos_multiplos", cur);
                                      if (k === 0) updateRow(i, "numero_pedido", e.target.value);
                                    }}
                                    placeholder={`Pedido ${k + 1}`}
                                    data-testid={`lote-pedido-${i}-${k}`}
                                    className="h-8 text-sm" />
                                </div>
                              );
                            })}
                            <p className="text-[10px] text-blue-700 font-medium">
                              {qtd} pedidos → 1 entrega
                            </p>
                            <DupCallout dup={dup} testId={`lote-dup-msg-${i}`} />
                          </div>
                        )}
                      </TableCell>
                    </>
                  )}
                  <TableCell>
                    <Input type="number" step="0.01" min="0" inputMode="decimal"
                      value={r.valor}
                      onChange={(e) => updateRow(i, "valor", e.target.value)}
                      placeholder="R$ 0,00"
                      data-testid={`lote-valor-${i}`}
                      className="h-9 text-sm mono w-full min-w-[120px]" />
                  </TableCell>
                  <TableCell>
                    {rowIsSemTroco ? (
                      <div className="h-9 flex items-center px-2 rounded-md bg-slate-100 border border-slate-200 text-[11px] text-slate-500 italic" data-testid={`lote-troco-para-disabled-${i}`}>
                        Sem troco
                      </div>
                    ) : (
                      <Input type="number" step="0.01"
                        value={r.troco_para || ""}
                        onChange={(e) => updateRow(i, "troco_para", e.target.value)}
                        placeholder="Automático"
                        data-testid={`lote-troco-para-${i}`}
                        className="h-9 text-sm mono" />
                    )}
                  </TableCell>
                  <TableCell>
                    {rowIsSemTroco ? (
                      <div className="h-9 flex items-center px-2 rounded-md bg-slate-50 border border-slate-200 text-sm font-bold text-slate-500 mono" data-testid={`lote-sem-troco-${i}`}>
                        Sem troco
                      </div>
                    ) : (
                      <div className="h-9 flex items-center px-2 rounded-md bg-orange-50 border border-orange-200 text-sm font-extrabold text-orange-700 mono" data-testid={`lote-troco-${i}`}>
                        {fmtBRL(r.troco || 0)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Input value={r.observacao || ""} onChange={(e) => updateRow(i, "observacao", e.target.value)} maxLength={500}
                      placeholder="Opcional" data-testid={`lote-obs-${i}`} className="h-9 text-sm min-w-[160px]" />
                  </TableCell>
                  <TableCell className="text-center">
                    <label
                      className={`h-9 flex items-center justify-center gap-1 rounded-md border cursor-pointer transition ${r.buscar_receita ? "bg-orange-50/60 border-orange-300" : "bg-white border-orange-100 hover:border-orange-300"}`}
                      title="Buscar receita no cliente"
                    >
                      <input type="checkbox"
                        checked={!!r.buscar_receita}
                        onChange={(e) => updateRow(i, "buscar_receita", e.target.checked)}
                        className="accent-orange-600 w-4 h-4 cursor-pointer"
                        data-testid={`lote-buscar-receita-${i}`} />
                      {r.buscar_receita && (
                        <span className="text-[10px] font-bold text-orange-700 uppercase">Sim</span>
                      )}
                    </label>
                  </TableCell>
                  <TableCell>
                    <Button size="icon" variant="ghost" onClick={() => removeRow(i)} disabled={rows.length <= 1} data-testid={`lote-remove-${i}`}>
                      <Trash2 className="w-4 h-4 text-rose-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        Linhas com Valor ≥ 0.01 <b>OU</b> Nº Pedido <b>OU</b> Bairro serão salvas. As demais são ignoradas.
        Cada linha define seu próprio <b>Tipo</b>, <b>Forma de Pagamento</b> e <b>Troco</b>.
        Se a coluna <b>Qtd</b> for maior que 1, todos os números viram <b>uma única entrega</b> no banco (conta como 1 no dashboard).
      </p>
      <div className="mt-2 space-y-1 text-xs">
        <p className="text-rose-700 flex items-center gap-1.5">
          <Ban className="w-3.5 h-3.5" />
          Linhas em <b>vermelho</b> possuem nº de pedido já em uso por entrega ativa <b>(Aguardando/Em Trânsito/Concluída)</b> — o salvamento é bloqueado até corrigir.
        </p>
        <p className="text-amber-700 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" />
          Linhas em <b>amarelo</b> conflitam apenas com entregas <b>Remarcadas/Canceladas</b> — permite salvar (número pode ser reutilizado).
        </p>
      </div>

      {qa && (
        <QuickAddDialog
          open={!!qa}
          onOpenChange={(o) => !o && setQa(null)}
          resource={qa.resource}
          endpoint={qa.endpoint}
          label={qa.label}
          extras={qa.extras || []}
          onAdded={(doc) => {
            onCatalogAdd?.(qa.resource, doc);
            updateRow(qa.rowIndex, qa.field, doc.id);
            if (qa.field === "bairro_id" && doc?.regiao_id) updateRow(qa.rowIndex, "regiao_id", doc.regiao_id);
            setQa(null);
          }}
        />
      )}
    </div>
  );
}
